/**
 * Formatting utility functions
 * All date/time operations use the configured timezone from timezoneStore
 */

import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ar';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import updateLocale from 'dayjs/plugin/updateLocale';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { CURRENCY } from '@/constants/currency';
import { getTimezone } from '@/stores/timezoneStore';

// Configure dayjs plugins
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);
dayjs.extend(updateLocale);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale('en');

// Configure custom Arabic meridiem (AM/PM) to display ص/م
dayjs.updateLocale('en', {
  meridiem: (hour: number) => {
    return hour < 12 ? 'ص' : 'م';
  }
});

/**
 * Get dayjs instance with configured timezone
 * This is the central function for all timezone-aware operations
 */
function withTimezone(date: string | Date): dayjs.Dayjs {
  const tz = getTimezone();
  return dayjs(date).tz(tz);
}

/**
 * Get current time in configured timezone
 */
export function nowInTimezone(): dayjs.Dayjs {
  const tz = getTimezone();
  return dayjs().tz(tz);
}

/**
 * Format number as currency
 * @param amount - Number to format (returns '0' with currency symbol if null/undefined)
 * @param locale - Locale string (default: 'en-US' for English numbers)
 * @returns Formatted currency string with English numbers and currency symbol
 */
export function formatCurrency(
  amount: number | null | undefined,
  locale: string = 'en-US'
): string {
  const value = amount === null || amount === undefined || isNaN(amount) ? 0 : amount;
  
  // Format number in English (en-US locale) without currency style
  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: CURRENCY.decimals,
    maximumFractionDigits: CURRENCY.decimals,
  }).format(value);
  
  // Add currency symbol
  return `${formattedNumber} ${CURRENCY.symbol}`;
}

/**
 * Format date in English (DD/MM/YYYY format) using configured timezone
 * @param date - Date to format (returns '-' if null/undefined)
 */
export function formatDate(
  date: string | Date | null | undefined
): string {
  if (!date) return '-';
  const dayjsDate = withTimezone(date);
  if (!dayjsDate.isValid()) return '-';

  return dayjsDate.format('DD/MM/YYYY');
}


/**
 * Format date and time with Arabic AM/PM using configured timezone
 * @param date - Date to format (returns '-' if null/undefined)
 * @returns Formatted string like "15/01/2025 3:30 ص" or "15/01/2025 3:30 م"
 * @example
 * formatDateTime('2025-01-15T09:30:00') // "15/01/2025 9:30 ص"
 * formatDateTime('2025-01-15T15:30:00') // "15/01/2025 3:30 م"
 */
export function formatDateTime(
  date: string | Date | null | undefined
): string {
  if (!date) return '-';
  const dayjsDate = withTimezone(date);
  if (!dayjsDate.isValid()) return '-';

  return dayjsDate.format('DD/MM/YYYY h:mm A');
}

/**
 * Format time only with Arabic AM/PM using configured timezone
 * @param date - Date to format
 * @returns Formatted time string like "9:30 ص" or "3:30 م"
 * @example
 * formatTime('2025-01-15T09:30:00') // "9:30 ص"
 * formatTime('2025-01-15T15:30:00') // "3:30 م"
 */
export function formatTime(date: string | Date): string {
  return withTimezone(date).format('h:mm A');
}

/**
 * Format date relative to now (e.g., "منذ ساعتين")
 * @param date - Date to format (returns '-' if null/undefined)
 */
export function formatRelativeTime(
  date: string | Date | null | undefined
): string {
  if (!date) return '-';
  const dayjsDate = dayjs(date);
  if (!dayjsDate.isValid()) return '-';

  return dayjsDate.locale('ar').fromNow();
}

/**
 * Format number with specified decimals
 * @param value - Number to format (returns '0' if null/undefined)
 * @param decimals - Number of decimal places (default: 2)
 */
export function formatNumber(value: number | null | undefined, decimals: number = 0): string {
  if (value === null || value === undefined || isNaN(value)) {
    return '0';
  }
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format amount for table display (without currency symbol)
 * Used in tables where currency is shown separately
 */
export function formatAmount(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '0';
  }
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/**
 * Parse decimal from string or number, handling Arabic numerals
 * @param value - Value to parse
 * @returns Parsed number (0 if invalid)
 */
export function parseDecimal(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return 0;

  if (typeof value === 'number') {
    return isNaN(value) ? 0 : value;
  }

  // Convert Arabic numerals to Western numerals
  const arabicToWestern: Record<string, string> = {
    '٠': '0',
    '١': '1',
    '٢': '2',
    '٣': '3',
    '٤': '4',
    '٥': '5',
    '٦': '6',
    '٧': '7',
    '٨': '8',
    '٩': '9',
  };

  let normalized = value;
  Object.entries(arabicToWestern).forEach(([arabic, western]) => {
    normalized = normalized.replace(new RegExp(arabic, 'g'), western);
  });

  // Remove any non-numeric characters except decimal point and minus sign
  normalized = normalized.replace(/[^\d.-]/g, '');

  const parsed = parseFloat(normalized);
  return isNaN(parsed) ? 0 : parsed;
}

/**
 * Format large numbers with abbreviations (K, M, B)
 */
export function formatCompactNumber(num: number): string {
  if (num >= 1000000000) {
    return `${(num / 1000000000).toFixed(1)} مليار`;
  }
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)} مليون`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)} ألف`;
  }
  return formatNumber(num);
}

/**
 * Format number as percentage
 */
export function formatPercentage(value: number, decimals: number = 1): string {
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format file size
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 بايت';

  const k = 1024;
  const sizes = ['بايت', 'كيلوبايت', 'ميغابايت', 'غيغابايت'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Format phone number
 */
export function formatPhoneNumber(phone: string): string {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX for 10 digits
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }

  // Format as +XX XXX XXX XXXX for international numbers
  if (cleaned.length === 12) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2, 5)} ${cleaned.slice(5, 8)} ${cleaned.slice(8)}`;
  }

  // Return as is if doesn't match expected length
  return phone;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, length: number): string {
  if (text.length <= length) return text;
  return text.slice(0, length) + '...';
}

/**
 * Convert date to input value format (YYYY-MM-DD) using configured timezone
 * Use for date-only fields (filters, date pickers without time)
 */
export function toInputDate(date: string | Date): string {
  return withTimezone(date).format('YYYY-MM-DD');
}

/**
 * Convert date to start of day (00:00:00.000) ISO string for API queries
 * Use for startDate in date range filters
 */
export function toInputStartDate(date: string | Date): string {
  return withTimezone(date).startOf('day').toISOString();
}

/**
 * Convert date to end of day (23:59:59.999) ISO string for API queries
 * Use for endDate in date range filters to include all transactions from that day
 */
export function toInputEndDate(date: string | Date): string {
  return withTimezone(date).endOf('day').toISOString();
}

/**
 * Convert date to ISO string format
 * Use for API submissions where time is important
 * Handles timezone conversion properly
 */
export function toISODateTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString();
}

/**
 * Get current date and time as ISO string
 * Use as default value for datetime fields in forms
 */
export function getCurrentDateTime(): Date {
  return new Date();
}

/**
 * Get current date (start of day in configured timezone) for date-only fields
 * Use as default value for date-only fields in forms
 */
export function getCurrentDate(): string {
  return nowInTimezone().format('YYYY-MM-DD');
}

/**
 * Get day of week in Arabic using configured timezone
 */
export function getDayOfWeek(date: string | Date): string {
  return withTimezone(date).format('dddd');
}

/**
 * Get month name in Arabic using configured timezone
 */
export function getMonthName(date: string | Date): string {
  return withTimezone(date).format('MMMM');
}

/**
 * Check if date is today (in configured timezone)
 */
export function isToday(date: string | Date): boolean {
  return withTimezone(date).isSame(nowInTimezone(), 'day');
}

/**
 * Check if date is yesterday (in configured timezone)
 */
export function isYesterday(date: string | Date): boolean {
  return withTimezone(date).isSame(nowInTimezone().subtract(1, 'day'), 'day');
}

/**
 * Get start of day (in configured timezone)
 */
export function startOfDay(date: string | Date): Date {
  return withTimezone(date).startOf('day').toDate();
}

/**
 * Get end of day (in configured timezone)
 */
export function endOfDay(date: string | Date): Date {
  return withTimezone(date).endOf('day').toDate();
}

/**
 * Get start of month (in configured timezone)
 */
export function startOfMonth(date: string | Date): Date {
  return withTimezone(date).startOf('month').toDate();
}

/**
 * Get end of month (in configured timezone)
 */
export function endOfMonth(date: string | Date): Date {
  return withTimezone(date).endOf('month').toDate();
}
